import { useState, useEffect } from 'react';
import { Users, ShoppingCart, Store, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { adminAPI } from '@/lib/api';

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Sellers', 
      value: stats?.total_sellers || 0, 
      icon: Users,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    { 
      title: 'Active Sellers', 
      value: stats?.active_sellers || 0, 
      icon: Store,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    { 
      title: 'Total Orders', 
      value: stats?.total_orders || 0, 
      icon: ShoppingCart,
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-home">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Platform overview and management</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-20 mb-4" />
                <div className="h-8 bg-muted rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {statCards.map((stat, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`h-10 w-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Monthly Trend */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Monthly Orders Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-4 h-40">
                {stats?.monthly_trend?.map((month, index) => {
                  const maxOrders = Math.max(...stats.monthly_trend.map(m => m.orders), 1);
                  const height = (month.orders / maxOrders) * 100;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-primary/80 rounded-t transition-all duration-500"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        {month.month.split('-')[1]}
                      </p>
                      <p className="text-sm font-medium">{month.orders}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
